
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  limit
} from "firebase/firestore";
import { db } from "./config";

export const getTransactionsByUserId = async (userId: string) => {
  try {
    const q = query(
      collection(db, "transactions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    }));
  } catch (error) {
    console.error("Error getting transactions: ", error);
    throw error;
  }
};

export const addTransaction = async (
  userId: string, 
  amount: number, 
  description: string
) => {
  try {
    console.log("Adding transaction:", { userId, amount, description });

    // Determine the running balance before this transaction.
    // Prefer the currentBalance of the most-recent existing transaction;
    // fall back to the denormalized users.balance for accounts that predate
    // this refactor (or when there are no prior transactions).
    const latestTxQuery = query(
      collection(db, "transactions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const latestTxSnap = await getDocs(latestTxQuery);
    let prevBalance: number;
    if (!latestTxSnap.empty && latestTxSnap.docs[0].data().currentBalance !== undefined) {
      // Modern transaction — use its embedded running balance directly.
      prevBalance = Number(latestTxSnap.docs[0].data().currentBalance);
    } else {
      // Either no prior transactions, or latest is a legacy doc without
      // currentBalance — fall back to the denormalized users.balance.
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      prevBalance = userDoc.exists() ? Number(userDoc.data().balance) || 0 : 0;
    }

    const transactionAmount = Number(amount);
    const newCurrentBalance = prevBalance + transactionAmount;

    console.log("Adding transaction with running balance:", {
      userId,
      prevBalance,
      amount: transactionAmount,
      newCurrentBalance,
    });

    // Update the user's balance so the cloud function and UI stay in sync
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      balance: newCurrentBalance,
      updatedAt: serverTimestamp()
    });

    // Write the transaction doc with the running balance embedded.
    // This write triggers the sendTransactionEmail cloud function.
    const transactionRef = await addDoc(collection(db, "transactions"), {
      userId,
      amount: transactionAmount,
      description,
      currentBalance: newCurrentBalance,
      createdAt: serverTimestamp()
    });

    return transactionRef.id;
  } catch (error) {
    console.error("Error adding transaction: ", error);
    throw error;
  }
};

export const deleteTransaction = async (transactionId: string, userId: string) => {
  try {
    // Only the most-recent transaction for a user may be deleted.
    // Deleting an earlier entry would corrupt every subsequent currentBalance.
    const latestTxQuery = query(
      collection(db, "transactions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const latestTxSnap = await getDocs(latestTxQuery);

    if (latestTxSnap.empty) {
      throw new Error("No transactions found for this user");
    }

    const latestTxDoc = latestTxSnap.docs[0];
    if (latestTxDoc.id !== transactionId) {
      throw new Error(
        "Only the most recent transaction can be deleted. " +
        "Earlier transactions cannot be removed individually."
      );
    }

    const transactionRef = doc(db, "transactions", transactionId);
    const transactionData = latestTxDoc.data();
    const deletedCurrentBalance = transactionData.currentBalance;
    const deletedAmount = transactionData.amount;

    const userRef = doc(db, "users", userId);

    // Derive the balance that should remain after this transaction is removed.
    // For modern docs: currentBalance already holds the running total after this tx.
    // For legacy docs without currentBalance: read users.balance and subtract.
    let balanceAfterDeletion: number;
    if (deletedCurrentBalance !== undefined) {
      balanceAfterDeletion = deletedCurrentBalance - deletedAmount;
    } else {
      const userDoc = await getDoc(userRef);
      const currentUserBalance = userDoc.exists() ? Number(userDoc.data().balance) || 0 : 0;
      balanceAfterDeletion = currentUserBalance - deletedAmount;
    }

    // Update the user doc
    await updateDoc(userRef, {
      balance: balanceAfterDeletion,
      updatedAt: serverTimestamp()
    });

    // Delete the transaction doc
    await deleteDoc(transactionRef);

    return true;
  } catch (error) {
    console.error("Error deleting transaction: ", error);
    throw error;
  }
};

// Helper function to update transaction user IDs (used during user linking)
export const updateTransactionUserId = async (oldUserId: string, newUserId: string) => {
  try {
    const transactionsQuery = query(
      collection(db, "transactions"),
      where("userId", "==", oldUserId)
    );
    
    const transactionDocs = await getDocs(transactionsQuery);
    const updatePromises = transactionDocs.docs.map(transactionDoc => 
      updateDoc(transactionDoc.ref, { userId: newUserId })
    );
    
    await Promise.all(updatePromises);
    console.log("Updated", updatePromises.length, "transactions");
  } catch (error) {
    console.error("Error updating transaction user IDs: ", error);
    throw error;
  }
};

// Helper function to delete all transactions for a user (used when removing child)
export const deleteTransactionsByUserId = async (userId: string) => {
  try {
    const q = query(
      collection(db, "transactions"),
      where("userId", "==", userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    const deletePromises = querySnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
  } catch (error) {
    console.error("Error deleting transactions by user ID: ", error);
    throw error;
  }
};


